import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useStyles, ModalSubmitButton, ModalCancelButton } from './style/style';
import { Grid } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import { InputGuestField } from './InputGuestField';
import { CreatedGuestField } from './CreatedGuestField';
import { CurrentGuestField } from './CurrentGuestField';
import { createFileShareUrl } from '../../../apicalls/cloud/shareFile';
import { createPictureShareUrl } from '../../../apicalls/cloud/sharePicture';
import type {
  TShareModal,
  TShareList,
  TCrestedGuest,
  TCurrentGuest
} from '../../../types/CloudStorageModal';

type Tprops = {
  folderId: number;
  folderName: string;
  constructId: number;
  setOpenShareFolderModal: React.Dispatch<React.SetStateAction<boolean>>;
  currentGuestList: TCurrentGuest[];
  getGuestList: () => void;
};

// 初期データ
const shareListDefaultValues: TShareList = {
  name: '',
  email: '',
  expiration_at: '',
  uploadable: false
};
const useFormDefaultValues: TShareModal = {
  list: [shareListDefaultValues]
};

// フォルダ共有
export const FolderShareField: React.FC<Tprops> = (props: Tprops) => {
  const {
    folderId,
    folderName,
    constructId,
    setOpenShareFolderModal,
    currentGuestList,
    getGuestList
  } = props;

  const history = useHistory();

  // スタイル読み込み
  const classes = useStyles();
  // 作成された共有先のリスト
  const [createdGuestList, setCreatedGuestList] = useState<TCrestedGuest[]>([]);

  // hook-formの初期設定
  const methods = useForm<TShareModal>({
    defaultValues: useFormDefaultValues
  });
  const { getValues, control, reset } = methods;
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'list'
  });

  const AddShareList = () => {
    append(shareListDefaultValues);
  };
  const deleteShareList = (index: number) => {
    remove(index);
  };
  const onChangeUploadable = (index: number) => {
    const data = getValues('list')[index];
    const newUploadable = data.uploadable === true ? false : true;
    const newData = { ...data, uploadable: newUploadable };
    update(index, newData);
  };

  const onSubmit = (data: TShareModal) => {
    const shareList = data.list; // ユーザーが送信した値（配列）
    if (shareList.length === 0) return;

    const resLinkUrl: TCrestedGuest[] = []; // リンクURLのレスポンスデータを一時保存する配列（shareListをループする度にpushする）

    shareList.forEach(el => {
      const params = {
        name: el.name,
        construct_id: constructId,
        folder_id: folderId,
        email: el.email,
        expiration_at: el.expiration_at === '' ? null : el.expiration_at, // 空文字だとエラーになるため、nullで送信
        uploadable: el.uploadable
      };

      // 開いているページによって共有URL生成のAPIを変更
      const createShareUrl =
        history.location.pathname === '/files'
          ? createFileShareUrl
          : createPictureShareUrl;
      createShareUrl(params)
        // レスポンスに不要なmessageが含まれているため、型を追加した後に削除
        .then((resData: TCrestedGuest & { message?: string }) => {
          delete resData.message;
          resLinkUrl.push(resData);
        });
    });

    setCreatedGuestList(resLinkUrl);

    // 「リンク発行」ボタンを押下後に、入力フィールドとボタンフィールドを非表示にし、リンクフィールドを表示させるが、
    // なぜかレンダリングされなかったため、やむを得ずsetTimeoutでremove（ユーザー入力値の全削除）を遅らせてレンダリングさせている
    setTimeout(() => {
      remove();
      reset(); //初期値にリセット
      getGuestList();
    }, 500);
  };

  return (
    <FormProvider {...methods}>
      <div className={classes.paper}>
        <Grid container>
          {/* タイトル */}
          <Grid item xs={12} className={classes.modalTitle}>
            <h1 style={{ fontSize: 24, textAlign: 'center', margin: 20 }}>
              「{folderName}」<br />
              フォルダを外部に共有
            </h1>
          </Grid>

          {createdGuestList && createdGuestList.length === 0 && (
            <>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Grid container>
                  {/* 入力フィールド */}
                  <Grid
                    item
                    xs={12}
                    style={{
                      padding: 24,
                      maxHeight: '35vh',
                      overflowY: 'auto'
                    }}
                  >
                    <Grid container justify='center'>
                      {fields &&
                        fields.map((field, index) => {
                          return (
                            <InputGuestField
                              key={field.id}
                              index={index}
                              onChangeUploadable={() =>
                                onChangeUploadable(index)
                              }
                              deleteShareList={() => deleteShareList(index)}
                              fieldsCount={fields.length}
                            />
                          );
                        })}

                      <Grid xs={2}>
                        <div
                          style={{
                            marginTop: 16,
                            textAlign: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={AddShareList}
                        >
                          ＋追加する
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* ボタンフィールド */}
                  <Grid item xs={12} className={classes.modalFooter}>
                    <ModalCancelButton
                      variant='contained'
                      onClick={() => setOpenShareFolderModal(false)}
                    >
                      キャンセル
                    </ModalCancelButton>

                    <ModalSubmitButton variant='contained' type='submit'>
                      リンク発行
                    </ModalSubmitButton>
                  </Grid>
                </Grid>
              </form>

              {/* 現在のフォルダ共有先 */}
              {currentGuestList.length > 0 && (
                <Grid container>
                  <Grid item xs={12} style={{ backgroundColor: '#F5F5F5' }}>
                    <div
                      style={{ display: 'flex', marginLeft: 50, marginTop: 24 }}
                    >
                      <PeopleAltIcon />
                      <p style={{ marginLeft: 8 }}>現在のフォルダ共有先</p>
                    </div>

                    <Grid
                      item
                      xs={12}
                      style={{
                        margin: '8px 42px',
                        padding: '16px 8px',
                        maxHeight: '20vh',
                        overflowY: 'auto'
                      }}
                    >
                      {currentGuestList.map((currentGuest: TCurrentGuest) => {
                        return (
                          <CurrentGuestField
                            key={currentGuest.id}
                            currentGuest={currentGuest}
                            getGuestList={getGuestList}
                          />
                        );
                      })}
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {createdGuestList && createdGuestList.length > 0 && (
            <>
              {/* リンクURLフィールド（コピーとメール送信） */}
              <Grid
                item
                xs={12}
                style={{ padding: 36, maxHeight: '50vh', overflowY: 'auto' }}
              >
                <Grid container justify='center'>
                  {createdGuestList.map(createdGuest => {
                    return (
                      <CreatedGuestField
                        key={createdGuest.id}
                        createdGuest={createdGuest}
                      />
                    );
                  })}
                </Grid>
              </Grid>

              {/* タイトル左側の＜アイコン（戻る） */}
              <ArrowBackIosIcon
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  top: 40,
                  left: 20
                }}
                onClick={() => setCreatedGuestList([])}
              />
            </>
          )}

          {/* 右上の×アイコン（モーダル閉じる） */}
          <CloseIcon
            style={{
              cursor: 'pointer',
              position: 'absolute',
              top: 30,
              right: 30
            }}
            fontSize='large'
            onClick={() => setOpenShareFolderModal(false)}
          />
        </Grid>
      </div>
    </FormProvider>
  );
};
